import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import UAParser from 'ua-parser-js';
import { Label } from '@ets/design-system';

import { useSelector } from '_common/hooks';
import { useListDevicesQuery, useDeleteDeviceMutation } from '../../UserSettingsApi';
import { selectAccounts } from 'Auth/redux/localStorageSlice';

import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';
import styles from './DevicesTable.module.scss';

type RowData = Device & { id: string };

const DevicesTable = () => {
  const intl = useIntl();
  const userId = useSelector((state) => state.auth.userId);
  const accounts = useSelector(selectAccounts);

  const { data: devices, isFetching } = useListDevicesQuery();
  const [deleteDevice] = useDeleteDeviceMutation();

  const columns = useMemo<TableColumn<RowData>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        minWidth: 176,
        header: intl.formatMessage({ id: 'settings.security.name' }),
        cell: (info) => {
          const device = info.row.original;

          return (
            <Cell testId={`${device.token}-name-column`}>
              {device.name === 'AUTO_VERIFIED'
                ? intl.formatMessage({ id: 'global.notSpecified' })
                : device.name}
            </Cell>
          );
        },
      },
      {
        id: 'platform',
        accessorKey: 'last_user_agent',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({ id: 'settings.security.platform' }),
        cell: (info) => {
          const device = info.row.original;

          return (
            <Cell testId={`${device.token}-platform-column`}>
              {parsePlatformValue(device.last_user_agent)}
            </Cell>
          );
        },
      },
      {
        id: 'lastAccess',
        accessorKey: 'creation',
        enableSorting: false,
        size: 169,
        header: intl.formatMessage({ id: 'settings.security.lastAccess' }),
        cell: (info) => {
          const device = info.row.original;

          return (
            <Cell testId={`${device.token}-last-access-column`}>
              <Cell.DateTime date={{ date: device.creation }} time={{ time: device.creation }} />
            </Cell>
          );
        },
      },
      {
        id: 'ipAdress',
        accessorKey: 'last_ip_address',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'settings.security.ipAdress' }),
        cell: (info) => {
          const device = info.row.original;

          return <Cell testId={`${device.token}-ip-adress-column`}>{device.last_ip_address}</Cell>;
        },
      },
      {
        id: 'status',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'STATUS' }),
        cell: (info) => {
          const device = info.row.original;

          if (device.token !== accounts[userId].device) {
            return null;
          }

          return (
            <Cell testId={`${device.token}-current-column`}>
              <Label size="s" fullWidth variant="green" testId="current">
                <FormattedMessage id="global.current" />
              </Label>
            </Cell>
          );
        },
      },
    ];
  }, []);

  const parsePlatformValue = (platform: string | undefined) => {
    const parser = new UAParser();
    if (platform) {
      parser.setUA(platform);
    }
    const result = parser.getResult();
    const str = intl.formatMessage(
      { id: 'settings.security.on' },
      {
        operatingSystem: result.os.name,
        browser: result.browser.name,
        version: result.browser.version,
      },
    );

    return str;
  };

  const rowsData = useMemo(() => {
    if (!devices) {
      return [];
    }

    return devices.map<TableRow<RowData>>((device) => {
      return {
        id: device.token,
        ...device,
        suffix: {
          actions: [
            {
              label: intl.formatMessage({ id: 'global.remove' }),
              onClick: () => {
                deleteDevice(device.token);
              },
            },
          ],
        },
      };
    });
  }, [devices]);

  return (
    <div className={styles.root}>
      <Table
        identity="userPreferences/security_devices"
        options={{ columns, data: rowsData }}
        renderFooter={() => null}
        isLoading={isFetching}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="userPreferences_security_devices"
        selectable={false}
        virtualized={false}
        height={'100%'}
      />
    </div>
  );
};

export default DevicesTable;
